<template>
  <div
    class="flex flex-col rounded-2xl border border-slate-300 px-4 py-2 dark:border-dark-3 dark:bg-dark-2/50"
    :class="{ 'ml-4': !adapterService }"
  >
    <header class="flex justify-end">
      <em
        v-if="isNewAdapter && adapterService"
        class="mdi mdi-close cursor-pointer text-2xl text-slate-500 dark:text-light-1"
        @click="removeAdapter(localAdapter)"
      />
    </header>

    <BaseDropdown
      v-model:selection="localAdapter.customizedNumber"
      label="Adapter"
      dropdown-text="Adapter wählen"
      :options-list="adapterTypeList"
      :recent-options-list="recommendedAdaptersPerSession"
      :disabled="disabled"
    />

    <div class="relative md:flex md:gap-4">
      <BaseFormInput
        v-model="localAdapter.customizedNumber"
        class="md:basis-1/2"
        :validation="v$?.adapter.customizedNumber"
        :disabled="disabled"
        type="number"
        label="KALO-Artikel"
      >
        <template v-if="!disabled" #trailing>
          <span
            class="mdi mdi-qrcode-scan text-3xl"
            @click="openDeviceCodeScanner"
          />
        </template>
      </BaseFormInput>
      <BaseFormInput
        v-model="localAdapter.productKey"
        class="md:basis-1/2"
        :validation="v$?.adapter.productKey"
        :disabled="disabled"
        label="Lieferant-Artikel"
      />
    </div>
    <div v-if="adapterService">
      <BaseDropdown
        v-model:selection="adapterService.type"
        label="Serviceleistung"
        dropdown-text="Serviceleistung wählen"
        :options-list="adapterServiceTypeList"
        :clearable="true"
        @update:selection="changeAdapterServiceType"
      />
      <div>
        <div v-if="adapterService.type" class="relative md:flex md:gap-4">
          <BaseDropdown
            v-model:selection="adapterService.reason"
            test-id="dropdown_Grund für Montage Korrektur"
            label="Grund auswählen"
            dropdown-text="Serviceleistung wählen"
            :options-list="adapterServiceList"
            class="md:basis-3/4"
            @update:selection="changeAdapterServiceReason"
          />
          <BaseFormInput
            v-model="localDateTime"
            label="Servicedatum"
            placeholder="Datum"
            type="date"
            disabled
          />
        </div>
        <div v-if="adapterService.reason === AdapterServiceReasonEnum.OTHER">
          <BaseTextarea
            v-model:input-text="adapterService.note"
            test-id="adapter-service-note"
            label="Notiz"
          />
        </div>
      </div>
    </div>

    <BaseInformation
      v-if="showCounterPressureWarning"
      :status="Status.WARNING"
      information-text="Bitte den Ventilgegendruck erneut nach der Installation aller Adapter messen.
        Die vorherige Messung wird durch diese Angabe überschrieben."
    >
    </BaseInformation>
  </div>
  <div v-if="isCodeScanActive">
    <DeviceCodeScanner
      :device-type="AccessoryType.SRT_ADAPTER"
      @scan-result="scanResult($event)"
    />
  </div>
</template>
<script setup lang="ts">
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import { Adapter } from '@/models/installationPoint/Accessory';
import { computed, inject, ref, watch, WritableComputedRef } from 'vue';
import DeviceCodeScanner from '@/components/UI/Scanner/DeviceCodeScanner.vue';
import {
  AccessoryType,
  adapterDemountReasonList,
  adapterExchangeReasonList,
  adapterMountReasonList,
  adapterRepairReasonList,
  AdapterServiceTypeEnum,
  adapterServiceTypeList,
  AdapterServiceReasonEnum,
  adapterTypeList,
  resolveAccessoryService,
} from '@/enums/device';
import { useValidation } from '@/composables/useValidation';
import { SRTAdapterScanResult } from '@/models/scanner/ScanResult';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { Status } from '@/enums/generic';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import { instanceToInstance } from 'class-transformer';
import { generateUniqueId } from '@/helpers/uuidUtils';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import useHardwareScanListener from '@/components/UI/Scanner/useHardwareScanListener';

const props = defineProps<{
  adapter: Adapter;
  removeAdapter: (arg0: Adapter) => void;
  disabled: boolean;
  isLastElement: boolean;
  isNewAdapter: boolean;
}>();

const adapterService = defineModel<AccessoryService>('adapterService');
const emit = defineEmits([
  'update:adapter',
  'onAdapterChange',
  'addAdapterService',
  'removeAdapterService',
  'addAdapter',
]);

const localDateTime = computed({
  get() {
    return adapterService.value?.timestamp.substr(0, 10);
  },
  set(val) {
    if (adapterService.value) {
      adapterService.value.timestamp = val ?? new Date(val).toISOString();
    }
  },
});

const localAdapter: WritableComputedRef<Adapter> = computed({
  get() {
    return props.adapter;
  },
  set(value) {
    emit('update:adapter', value);
  },
});

const isCounterPressureMeasureValid = inject(
  'isCounterPressureMeasureValid',
  false
);
const showCounterPressureWarning = computed(
  () =>
    localAdapter.value.customizedNumber &&
    localAdapter.value.customizedNumber !== '42050' &&
    props.isLastElement &&
    isCounterPressureMeasureValid?.value
);

const isCodeScanActive = ref(false);
const openDeviceCodeScanner = () => {
  return (isCodeScanActive.value = true);
};

watch(
  () => localAdapter.value.customizedNumber,
  (newValue) => {
    const adapter = adapterTypeList.find(
      (adapter) => adapter.value === newValue
    );
    if (adapter) {
      localAdapter.value.productKey = adapter.productKey;
      localAdapter.value.customizedNumber = adapter.customizedNumber;
      useInstallationPointStore().addToRecentAdapterList(adapter);
      emit('onAdapterChange', adapter);
    } else {
      localAdapter.value.productKey = '';
    }
  }
);

const newAdapter = ref<Adapter | undefined>(undefined);
const newAdapterService = ref<AccessoryService | undefined>(undefined);

watch(
  () => newAdapter.value,
  (newValue) => {
    if (newValue) {
      adapterService.value.newAccessoryId = newAdapter.value?.id;
      adapterService.value.isExchangedAdapter = true;
      emit('addAdapter', newAdapter.value);
    }
  }
);

const changeAdapterServiceType = (value) => {
  if (newAdapter.value || newAdapterService.value) {
    emit('removeAdapterService', {
      adapterService: newAdapterService.value,
      adapter: newAdapter.value,
    });
    adapterService.value.reason = undefined;
  }
  return;
};

const changeAdapterServiceReason = (newValue) => {
  if (adapterService.value?.type !== AdapterServiceTypeEnum.EXCHANGE) {
    return;
  }

  if (newAdapter.value || newAdapterService.value) {
    emit('removeAdapterService', {
      adapterService: newAdapterService.value,
      adapter: newAdapter.value,
    });
  }

  if (newValue === adapterExchangeReasonList[0].value) {
    newAdapter.value = instanceToInstance(localAdapter.value);
    newAdapter.value.id = generateUniqueId();
    newAdapter.value.mountedAt = new Date().toISOString();
  }
  if (newValue === adapterExchangeReasonList[1].value) {
    newAdapter.value = new Adapter();
    newAdapter.value.type = AccessoryType.SRT_ADAPTER;
    newAdapter.value.mountedAt = new Date().toISOString();
  }
};

const recommendedAdaptersPerSession = computed(() => {
  return useInstallationPointStore().recommendedAdaptersPerSession;
});

const scanResult = (scanDataObject: SRTAdapterScanResult | object) => {
  if (scanDataObject instanceof SRTAdapterScanResult) {
    localAdapter.value.productKey = scanDataObject.productKey;
    localAdapter.value.customizedNumber = scanDataObject.customizedNumber;
    localAdapter.value.unknownProperties = scanDataObject.unknownProperties;
  }

  isCodeScanActive.value = false;
  return;
};
const adapterServiceList = computed(() => {
  if (!adapterService.value) return [];
  const { reasonPool } = resolveAccessoryService(adapterService.value.type);
  return reasonPool as any;
});

const { v$ } = useValidation({
  adapter: localAdapter,
  accessoryService: adapterService,
});

useHardwareScanListener(scanResult, AccessoryType.SRT_ADAPTER);
</script>
